import {
  MandatePayer,
  MandatePayerOptionDto,
  QuoteMandateDto,
  UpdateQuoteMandateDto
} from 'api/quote';
import { toDayInput } from 'core/utils/dates';
import {
  QuoteMandateFormValues,
  quoteMandateFormConfig,
  QuoteMandateFormFieldNames
} from './quoteMandateForm';

/**
 * Maps API response to form values
 */
export const mapResponseToFormValues = (
  quoteMandate: QuoteMandateDto | null,
  selectedBankAccountId?: string
): QuoteMandateFormValues => {
  if (!quoteMandate || !quoteMandate.startDate) {
    return quoteMandateFormConfig.initialValues;
  }

  return {
    // Date calendaire : découpe de string, aucune conversion de fuseau
    [QuoteMandateFormFieldNames.StartDate]: toDayInput(quoteMandate.startDate),
    [QuoteMandateFormFieldNames.DueDate]: quoteMandate.dueDate?.month ?? null,
    [QuoteMandateFormFieldNames.Frequency]: quoteMandate.frequencyCode || '',
    [QuoteMandateFormFieldNames.Payer]: quoteMandate.payer ?? MandatePayer.Souscripteur,
    [QuoteMandateFormFieldNames.BankAccount]: selectedBankAccountId || ''
  };
};

/**
 * Maps form values to API command
 */
export const mapFormValuesToCommand = (
  values: QuoteMandateFormValues,
  frequencyId: string
): UpdateQuoteMandateDto => {
  return {
    // Date calendaire : string "yyyy-MM-dd" de bout en bout (jamais de Date sérialisé)
    startDate: values.startDate,
    dueDate:
      values.dueDate !== null
        ? {
            day: 1,
            month: values.dueDate
          }
        : undefined,
    frequencyId,
    bankAccountId: values.bankAccount,
    // Le débiteur du mandat est déduit par le back du payeur : le front n'envoie plus de personne.
    payer: values.payer
  };
};

/**
 * Gets the selected bank account ID from mandate data
 */
export const getSelectedBankAccountId = (
  quoteMandate: QuoteMandateDto | null
): string | undefined => {
  if (!quoteMandate) return undefined;

  // Le compte du mandat peut appartenir au cabinet (courtier encaisseur) : il n'est alors
  // pas dans la liste du souscripteur, seul l'id renvoyé par le back permet de le retrouver.
  if (quoteMandate.selectedBankAccountId) return quoteMandate.selectedBankAccountId;

  const selectedAccount = quoteMandate.bankAccounts.find((ba) => ba.isSelected);
  const defaultAccount = quoteMandate.bankAccounts.find((ba) => ba.isDefault);

  return selectedAccount?.id || defaultAccount?.id;
};

/**
 * Gets the payer option proposed by the back for the given payer: it carries the person whose
 * bank accounts are listed and on whom a new account is created.
 */
export const getPayerOption = (
  quoteMandate: QuoteMandateDto | null,
  payer: MandatePayer
): MandatePayerOptionDto | undefined =>
  quoteMandate?.payerOptions?.find((option) => option.code === payer);
